import { Injectable, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { environment } from '../environments/environment';

export enum ErrorCategory {
  NETWORK = 'network',
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  SERVER = 'server',
  CLIENT = 'client',
  UNKNOWN = 'unknown'
}

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export interface ErrorContext {
  user?: {
    id?: string;
    email?: string;
    role?: string;
  };
  action?: string;
  component?: string;
  route?: string;
  timestamp: number;
  userAgent?: string;
  url?: string;
  [key: string]: any;
}

export interface TrackedError {
  message: string;
  stack?: string;
  category: ErrorCategory;
  severity: ErrorSeverity;
  context: ErrorContext;
  error?: Error | any;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorTrackingService {
  private authService = inject(AuthService);
  private baseUrl = `${environment.apiBaseUrl}/v1`; // Use direct fetch to avoid circular dependency with ApiService
  
  // Error queue for batching (can be sent to external service)
  private errorQueue: TrackedError[] = [];
  private readonly MAX_QUEUE_SIZE = 100;
  private readonly FLUSH_INTERVAL = 30000; // 30 seconds
  
  // In-memory error storage (for development/debugging)
  private recentErrors: TrackedError[] = [];
  private readonly MAX_RECENT_ERRORS = 50;
  
  constructor() {
    // Flush errors periodically (can be configured to send to external service)
    setInterval(() => this.flushErrorQueue(), this.FLUSH_INTERVAL);
    
    // Capture global errors
    this.setupGlobalErrorHandlers();
  }

  /**
   * Track an error with categorization and context
   */
  trackError(
    error: Error | string,
    category: ErrorCategory = ErrorCategory.UNKNOWN,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    additionalContext?: Partial<ErrorContext>
  ): void {
    const errorMessage = error instanceof Error ? error.message : error;
    const stack = error instanceof Error ? error.stack : undefined;
    
    const context: ErrorContext = {
      ...this.getDefaultContext(),
      ...additionalContext,
      timestamp: Date.now()
    };

    const trackedError: TrackedError = {
      message: errorMessage,
      stack,
      category,
      severity,
      context,
      error: error instanceof Error ? this.sanitizeError(error) : error
    };

    // Add to recent errors
    this.recentErrors.unshift(trackedError);
    if (this.recentErrors.length > this.MAX_RECENT_ERRORS) {
      this.recentErrors.pop();
    }

    // Queue for batch processing
    this.errorQueue.push(trackedError);
    if (this.errorQueue.length > this.MAX_QUEUE_SIZE) {
      this.errorQueue.shift(); // Remove oldest if queue is full
    }

    // Log to console in development
    if (!this.isProduction()) {
      console.error(`[Error Tracking] ${category.toUpperCase()}/${severity.toUpperCase()}:`, trackedError);
    }

    // Send critical errors immediately
    if (severity === ErrorSeverity.CRITICAL) {
      this.sendErrorImmediately(trackedError);
    }
  }

  /**
   * Categorize HTTP errors automatically
   */
  trackHttpError(
    error: any,
    endpoint: string,
    method: string = 'GET',
    additionalContext?: Partial<ErrorContext>
  ): void {
    let category = ErrorCategory.NETWORK;
    let severity = ErrorSeverity.MEDIUM;

    if (error?.status) {
      const status = error.status;
      if (status === 401) {
        category = ErrorCategory.AUTHENTICATION;
        severity = ErrorSeverity.HIGH;
      } else if (status === 403) {
        category = ErrorCategory.AUTHORIZATION;
        severity = ErrorSeverity.HIGH;
      } else if (status === 400 || status === 422) {
        category = ErrorCategory.VALIDATION;
        severity = ErrorSeverity.LOW;
      } else if (status >= 500) {
        category = ErrorCategory.SERVER;
        severity = ErrorSeverity.HIGH;
      } else if (status === 429) {
        category = ErrorCategory.NETWORK;
        severity = ErrorSeverity.MEDIUM;
      }
    } else if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
      category = ErrorCategory.NETWORK;
      severity = ErrorSeverity.MEDIUM;
    }

    this.trackError(
      error?.message || error || 'HTTP Error',
      category,
      severity,
      {
        ...additionalContext,
        endpoint,
        method,
        httpStatus: error?.status,
        httpStatusText: error?.statusText
      }
    );
  }

  /**
   * Track validation errors
   */
  trackValidationError(
    errors: any,
    formName?: string,
    additionalContext?: Partial<ErrorContext>
  ): void {
    const errorMessage = Array.isArray(errors) 
      ? errors.map(e => e.message || e).join(', ')
      : (errors?.message || String(errors));

    this.trackError(
      errorMessage,
      ErrorCategory.VALIDATION,
      ErrorSeverity.LOW,
      {
        ...additionalContext,
        formName,
        validationErrors: errors
      }
    );
  }

  /**
   * Get recent errors (for debugging/admin panel)
   */
  getRecentErrors(limit: number = 10): TrackedError[] {
    return this.recentErrors.slice(0, limit);
  }

  /**
   * Get error statistics
   */
  getErrorStats(): {
    total: number;
    byCategory: Record<ErrorCategory, number>;
    bySeverity: Record<ErrorSeverity, number>;
  } {
    const stats = {
      total: this.recentErrors.length,
      byCategory: {} as Record<ErrorCategory, number>,
      bySeverity: {} as Record<ErrorSeverity, number>
    };

    this.recentErrors.forEach(error => {
      stats.byCategory[error.category] = (stats.byCategory[error.category] || 0) + 1;
      stats.bySeverity[error.severity] = (stats.bySeverity[error.severity] || 0) + 1;
    });

    return stats;
  }

  /**
   * Clear error history
   */
  clearErrors(): void {
    this.recentErrors = [];
    this.errorQueue = [];
  }

  /**
   * Get default context (user info, route, etc.)
   */
  private getDefaultContext(): ErrorContext {
    const user = this.authService.currentUser();
    const profile = this.authService.currentUserProfile();

    return {
      timestamp: Date.now(),
      user: user || profile ? {
        id: user?.uid || profile?.id,
        email: user?.email || profile?.email,
        role: profile?.role || profile?.companyRole
      } : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
    };
  }

  /**
   * Setup global error handlers
   */
  private setupGlobalErrorHandlers(): void {
    if (typeof window === 'undefined') return;

    // Unhandled errors
    window.addEventListener('error', (event) => {
      this.trackError(
        event.error || event.message || 'Unhandled error',
        ErrorCategory.CLIENT,
        ErrorSeverity.HIGH,
        {
          component: 'Global',
          action: 'UnhandledError',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      );
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError(
        event.reason || 'Unhandled promise rejection',
        ErrorCategory.CLIENT,
        ErrorSeverity.HIGH,
        {
          component: 'Global',
          action: 'UnhandledPromiseRejection'
        }
      );
    });
  }

  /**
   * Send error to external service (placeholder for Sentry/LogRocket integration)
   */
  private sendErrorImmediately(error: TrackedError): void {
    // TODO: Integrate with external service (Sentry, LogRocket, etc.)
    // Example:
    // if (window.Sentry) {
    //   window.Sentry.captureException(error.error, {
    //     tags: { category: error.category, severity: error.severity },
    //     extra: error.context
    //   });
    // }
    
    console.error('[Critical Error]', error);
  }

  /**
   * Log error to backend (fire and forget - don't block user)
   * Uses lazy injection to avoid circular dependency with ApiService
   */
  async logError(
    error: Error | string,
    category: ErrorCategory = ErrorCategory.UNKNOWN,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    context?: Partial<ErrorContext>,
    companyId?: string
  ): Promise<void> {
    try {
      const errorMessage = error instanceof Error ? error.message : error;
      const stack = error instanceof Error ? error.stack : undefined;
      
      const errorContext: ErrorContext = {
        ...this.getDefaultContext(),
        ...context,
        timestamp: Date.now()
      };

      // Map ErrorCategory enum to backend category
      const backendCategory = category === ErrorCategory.AUTHENTICATION || category === ErrorCategory.AUTHORIZATION
        ? 'validation' // Map auth errors to validation category for backend
        : category === ErrorCategory.CLIENT
        ? 'client'
        : category === ErrorCategory.SERVER
        ? 'server'
        : category === ErrorCategory.NETWORK
        ? 'network'
        : 'unknown';

      // Map ErrorSeverity enum to backend severity
      const backendSeverity = severity.toLowerCase() as 'low' | 'medium' | 'high' | 'critical';

      // Use fetch directly to avoid circular dependency with ApiService
      // Get token for authentication
      let token: string | null = null;
      try {
        token = await this.authService.getToken();
      } catch (tokenError) {
        // If token fetch fails, skip backend logging (don't block)
        return;
      }

      // Send to backend using fetch directly (fire and forget - don't await)
      fetch(`${this.baseUrl}/errors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: errorMessage,
          stack,
          category: backendCategory,
          severity: backendSeverity,
          context: errorContext,
          companyId,
        }),
      }).catch((err) => {
        // Log to console if backend logging fails (don't throw - this is fire and forget)
        console.error('Failed to log error to backend:', err);
      });
    } catch (err) {
      // Don't throw - error logging should never fail user operations
      console.error('Error in logError:', err);
    }
  }

  /**
   * Flush error queue (send to backend in batches)
   */
  private flushErrorQueue(): void {
    if (this.errorQueue.length === 0) return;

    const errorsToFlush = [...this.errorQueue];
    this.errorQueue = [];

    // Send errors to backend (fire and forget)
    errorsToFlush.forEach(error => {
      const companyId = error.context?.['companyId'] as string | undefined;
      this.logError(
        error.error || error.message,
        error.category,
        error.severity,
        error.context,
        companyId
      ).catch(() => {
        // Ignore errors - logging failures shouldn't affect app
      });
    });

  }

  /**
   * Sanitize error object (remove circular references, sensitive data)
   */
  private sanitizeError(error: Error): any {
    try {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack
      };
    } catch {
      return { message: String(error) };
    }
  }

  /**
   * Check if in production
   */
  private isProduction(): boolean {
    return typeof window !== 'undefined' && 
           (window.location.hostname.includes('web.app') || 
            window.location.hostname.includes('firebaseapp.com'));
  }
}
